// console-resize — the output console can be resized by dragging the divider above it
// (Trello ZUKcta6v), with the chosen height remembered across sessions.
//
// Wired from main.js in two parts, uniformly across every surface (like stage-prerun.js,
// NOT dispatched by data-surface):
//   applyStoredConsoleHeight(doc)  EAGER — needs only document.body + localStorage, so the
//                                  console never paints at the default height and then snaps
//                                  to the stored one on every load.
//   wireConsoleResize(core)        LATE — alongside wireStagePrerun(core), because the
//                                  divider's interactive path calls getEditor().resize().
//
// MECHANISM: the dragged size is an inline `--console-h` custom property on document.body,
// gated by a `body.console-resized` marker class, sizing `.runner-console-wrap` (NOT
// #runner-console). Three consequences are load-bearing:
//   * On BODY, not the wrap — an inline declaration beats any author rule on the SAME
//     element, so `.runner-console-wrap:fullscreen { --console-h: auto }` could never win.
//     Set on body it merely inherits, and element-local rules take precedence as intended.
//   * Sizing the WRAP means the stdin row and the runner's Reload button (both appended
//     INSIDE the wrap by editor-core) take their space from the console rather than pushing
//     the layout past the viewport, and python-mode fullscreen still expands correctly.
//   * The divider therefore tracks the wrap's top edge 1:1, with no arithmetic over siblings.
//
// The stored value is a FRACTION OF THE VIEWPORT (one global value is replayed across
// devices by design, so raw pixels would be meaningless), applied as a pixel value
// recomputed at every clamp. A clamp forced by layout or viewport is NEVER written back —
// only an explicit drag-end, keyboard step or reset persists.
//
// MUST NEVER THROW (storage is wrapped end to end): applyStoredConsoleHeight runs inside
// main.js's IIFE and wireConsoleResize inside its loadProject().then(), where a throw would
// paint "Could not load the editor." over a working editor and skip window.__editorCore.

var STORAGE_KEY = 'pc.editor.console-height';

// Ceilings expressed in viewport terms, but always applied against the container's REAL
// available height — the instructions banner (<=34vh), review's panel (42%), the status
// bar, the stdin row and the Reload button all compete for the same space, so 30 + 60 is
// already unsatisfiable on some surfaces. The code+stage floor wins when they conflict.
var MAX_CONSOLE_VH = 0.6;
var MIN_SPLIT_VH = 0.3;

// The layout breakpoint below which the split stacks with 42vh floors of its own. Nothing
// is applied there, and the divider is display:none in the same media query.
var MIN_APPLY_WIDTH = 901;

var KEY_STEP_PX = 24;
// Only a change larger than this is written back — the ResizeObserver observes elements its
// own clamp resizes, so an exact-equality test would oscillate.
var EPSILON_PX = 1;

// The user's chosen height as a fraction of viewport height, or null when they have never
// dragged (or have reset). This is the single in-memory source of truth; the DOM property
// is derived from it, which is also what lets the module self-heal after pc-feedback's
// screenshot capture restores recorded style attributes over the top of it.
var currentFraction = null;
var lastAppliedPx = 0;
var aceResize = null;

// ---------------------------------------------------------------------------
// Pure, DOM-free bounds maths (unit-tested by test/editor/console-resize.test.js)
// ---------------------------------------------------------------------------

function num(v) {
  return (typeof v === 'number' && isFinite(v)) ? v : 0;
}

// Returns the clamped wrap height in px, or null when the stored height must NOT be
// applied at all — either the inputs are unusable, or the free space cannot honour BOTH the
// chrome-inclusive console minimum and the code+stage floor. Falling back to the default
// layout is preferable to guessing which constraint yields.
export function clampConsoleHeight(input) {
  input = input || {};
  var available = num(input.available);
  var viewport = num(input.viewport);
  var desired = num(input.desired);
  // Chrome-inclusive: .runner-console-wrap is a flex column whose other children are all
  // non-shrinkable (#runner-status, the divider, the Reload button, .runner-stdin). Only
  // #runner-console shrinks, so the minimum is those children plus readable console text —
  // otherwise the console collapses to zero and the stdin row overflows the dragged box.
  var chrome = Math.max(0, num(input.chrome));
  var minText = Math.max(0, num(input.minText));

  if (available <= 0 || viewport <= 0 || desired <= 0) { return null; }

  var min = chrome + minText;
  var max = Math.min(viewport * MAX_CONSOLE_VH, available - viewport * MIN_SPLIT_VH);
  if (max < min) { return null; }

  return Math.round(Math.min(Math.max(desired, min), max));
}

// Validates a value read back from storage: it must parse as a number strictly between 0
// and 1 exclusive. Anything else — non-numeric, negative, absurd or hostile — is rejected.
export function parseStoredFraction(raw) {
  if (typeof raw !== 'string' || !raw) { return null; }
  var value = parseFloat(raw);
  if (!isFinite(value) || value <= 0 || value >= 1) { return null; }
  return value;
}

// ---------------------------------------------------------------------------
// Storage (throw-safe end to end: setItem throws in Safari private mode / on quota /
// under a storage policy block, and getItem can return anything at all)
// ---------------------------------------------------------------------------

function storage(doc) {
  try {
    var view = doc && doc.defaultView;
    return (view && view.localStorage) || null;
  } catch (e) { return null; }
}

function readStored(doc) {
  try {
    var store = storage(doc);
    return store ? parseStoredFraction(store.getItem(STORAGE_KEY)) : null;
  } catch (e) { return null; }
}

function writeStored(doc, fraction) {
  try {
    var store = storage(doc);
    if (store) { store.setItem(STORAGE_KEY, String(fraction)); }
  } catch (e) { /* private mode / quota / blocked — the height still works this session */ }
}

function clearStored(doc) {
  try {
    var store = storage(doc);
    if (store) { store.removeItem(STORAGE_KEY); }
  } catch (e) { /* nothing to do */ }
}

// ---------------------------------------------------------------------------
// Measurement
// ---------------------------------------------------------------------------

function parts(doc) {
  if (!doc || !doc.querySelector) { return null; }
  var split = doc.querySelector('.editor-split');
  var wrap = doc.querySelector('.runner-console-wrap');
  if (!split || !wrap) { return null; }
  return { split: split, wrap: wrap, consoleEl: doc.getElementById('runner-console') };
}

function wide(doc) {
  try {
    var view = doc.defaultView;
    if (view && view.matchMedia) { return view.matchMedia('(min-width: ' + MIN_APPLY_WIDTH + 'px)').matches; }
    return !!view && view.innerWidth >= MIN_APPLY_WIDTH;
  } catch (e) { return false; }
}

// Every non-shrinkable child of the wrap except the console itself.
function measureChrome(wrap, consoleEl) {
  var total = 0;
  var kids = wrap.children || [];
  for (var i = 0; i < kids.length; i++) {
    if (kids[i] === consoleEl) { continue; }
    total += kids[i].offsetHeight || 0;
  }
  return total;
}

// Two readable lines of console text plus the console's own vertical padding.
function measureMinText(doc, consoleEl) {
  var lineHeight = 20;
  var padding = 16;
  try {
    var view = doc.defaultView;
    if (consoleEl && view && view.getComputedStyle) {
      var cs = view.getComputedStyle(consoleEl);
      var lh = parseFloat(cs.lineHeight);
      if (isFinite(lh) && lh > 0) { lineHeight = lh; }
      padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    }
  } catch (e) { /* fall back to the constants above */ }
  return Math.round(lineHeight * 2 + padding);
}

function measure(doc, els) {
  var view = doc.defaultView;
  var viewport = (view && view.innerHeight) || 0;
  var available = els.split.getBoundingClientRect().height + els.wrap.getBoundingClientRect().height;
  return {
    available: available,
    viewport: viewport,
    chrome: measureChrome(els.wrap, els.consoleEl),
    minText: measureMinText(doc, els.consoleEl)
  };
}

function bounds(dims) {
  var min = Math.max(0, dims.chrome) + Math.max(0, dims.minText);
  var max = Math.min(dims.viewport * MAX_CONSOLE_VH, dims.available - dims.viewport * MIN_SPLIT_VH);
  return { min: min, max: Math.max(min, max) };
}

// ---------------------------------------------------------------------------
// Applying / clearing
// ---------------------------------------------------------------------------

function relayoutAce() {
  try { if (aceResize) { aceResize(); } } catch (e) { /* Ace is optional here */ }
}

function clearApplied(doc) {
  try {
    if (!lastAppliedPx && !doc.body.classList.contains('console-resized')) { return; }
    doc.body.classList.remove('console-resized');
    doc.body.style.removeProperty('--console-h');
    lastAppliedPx = 0;
    relayoutAce();
  } catch (e) { /* nothing to do */ }
}

// Resolves currentFraction against the live layout and writes (or withdraws) the property.
// Returns the applied px, or 0 when nothing is applied.
function applyCurrent(doc, force) {
  var els = parts(doc);
  if (!els) { return 0; }
  // Below the breakpoint the stored height is not applied AT ALL — the stacked layout
  // already reserves 42vh each for the pane and the stage.
  if (currentFraction === null || !wide(doc)) { clearApplied(doc); return 0; }

  var dims = measure(doc, els);
  var px = clampConsoleHeight({
    available: dims.available,
    viewport: dims.viewport,
    chrome: dims.chrome,
    minText: dims.minText,
    desired: currentFraction * dims.viewport
  });
  if (px === null) { clearApplied(doc); return 0; }

  // The missing-property test is the self-heal: pc-feedback's screenshot capture restores
  // each ancestor's recorded style attribute, silently reverting --console-h mid-capture.
  var alreadySet = doc.body.style.getPropertyValue('--console-h');
  if (force || !alreadySet || Math.abs(px - lastAppliedPx) > EPSILON_PX) {
    doc.body.style.setProperty('--console-h', px + 'px');
    doc.body.classList.add('console-resized');
    lastAppliedPx = px;
    relayoutAce();
  }
  return px;
}

// ---------------------------------------------------------------------------
// ARIA
// ---------------------------------------------------------------------------

function syncAria(doc, divider) {
  try {
    var els = parts(doc);
    if (!divider || !els) { return; }
    var dims = measure(doc, els);
    var limits = bounds(dims);
    var now = Math.round(els.wrap.getBoundingClientRect().height);
    divider.setAttribute('aria-valuemin', String(Math.round(limits.min)));
    divider.setAttribute('aria-valuemax', String(Math.round(limits.max)));
    divider.setAttribute('aria-valuenow', String(now));
    // Raw pixels announce as meaningless, so the human-readable channel is console lines.
    // NEVER write into #runner-console — it is already an aria-live region labelled
    // "Code Output"; this element's own aria-valuetext is the announcement channel.
    var perLine = Math.max(1, dims.minText / 2);
    var lines = Math.max(0, Math.round((now - dims.chrome) / perLine));
    divider.setAttribute('aria-valuetext', lines + (lines === 1 ? ' line' : ' lines') + ' of output');
  } catch (e) { /* never throw out of a listener */ }
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

// EAGER: replay the stored height before the payload round-trip so the console never snaps.
export function applyStoredConsoleHeight(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.body) { return; }
    currentFraction = readStored(doc);
    if (currentFraction === null) { return; }
    applyCurrent(doc, true);
  } catch (e) { console.error(e); }
}

// LATE: the divider's interactive behaviour. Null-safe — a no-op on any template that does
// not render #console-divider inside the console wrap.
export function wireConsoleResize(core) {
  try {
    var doc = (typeof document !== 'undefined') ? document : null;
    if (!doc) { return; }
    var divider = doc.getElementById('console-divider');
    var els = parts(doc);
    if (!divider || !els) { return; }

    aceResize = function () {
      var ed = core && core.getEditor && core.getEditor();
      if (ed && ed.resize) { ed.resize(); }
    };

    // Ships hidden + aria-hidden so it is never a focusable separator that resizes nothing
    // (during the initial round-trip, and permanently on the loadProject() failure path).
    // BOTH must come off: a focusable role="separator" still carrying aria-hidden is an axe
    // aria-hidden-focus violation. Below 901px the CSS keeps it display:none regardless.
    divider.hidden = false;
    divider.removeAttribute('aria-hidden');

    var view = doc.defaultView;

    // Clamp the requested height into the measured bounds FIRST, so the fraction we store
    // is one that can actually be honoured.
    var commit = function (px, persist) {
      var dims = measure(doc, els);
      if (dims.viewport <= 0) { return; }
      var limits = bounds(dims);
      var target = Math.min(Math.max(num(px), limits.min), limits.max);
      currentFraction = target / dims.viewport;
      var applied = applyCurrent(doc, true);
      if (applied > 0) { currentFraction = applied / dims.viewport; }
      syncAria(doc, divider);
      if (persist && applied > 0) { writeStored(doc, currentFraction); }
    };

    // Reset REMOVES the class and CLEARS the property — it must not write the default pixel
    // value, or python-only mode would stay pinned instead of returning to filling the space.
    var reset = function () {
      currentFraction = null;
      clearApplied(doc);
      clearStored(doc);
      syncAria(doc, divider);
    };

    // --- pointer drag (covers mouse, touch and stylus in one path) -----------
    var dragBottom = 0;
    // Where inside the divider the pointer grabbed it. The divider is ~19px tall (taller under
    // pointer:coarse), so without this the first pointermove snaps the wrap's top edge to the
    // pointer — a visible jolt, and every later position offset by the grab point.
    var grabOffset = 0;
    divider.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) { return; }
      var rect = els.wrap.getBoundingClientRect();
      dragBottom = rect.bottom;
      grabOffset = e.clientY - rect.top;
      // Deliberately NOT preventDefault() here: cancelling pointerdown sets the prevent-mouse-event
      // flag, which suppresses the synthesized mousedown — taking the divider's focus with it and
      // putting the dblclick reset chain at risk. Text selection is already handled by
      // .console-divider / body.console-dragging { user-select: none }, and touch panning by
      // touch-action: none, so nothing here needs cancelling.
      try { divider.setPointerCapture(e.pointerId); } catch (err) { /* optional */ }
      try { divider.focus(); } catch (err) { /* optional */ }
      doc.body.classList.add('console-dragging');
    });
    divider.addEventListener('pointermove', function (e) {
      if (!doc.body.classList.contains('console-dragging')) { return; }
      if (e.preventDefault) { e.preventDefault(); }
      commit(dragBottom - (e.clientY - grabOffset), false);
    });
    var endDrag = function () {
      if (!doc.body.classList.contains('console-dragging')) { return; }
      doc.body.classList.remove('console-dragging');
      // Only an explicit user action persists — never a layout- or viewport-forced clamp.
      if (currentFraction !== null) { writeStored(doc, currentFraction); }
    };
    divider.addEventListener('pointerup', endDrag);
    divider.addEventListener('pointercancel', endDrag);

    // --- keyboard -----------------------------------------------------------
    // preventDefault() on EVERY handled key: this body scrolls, so Space would page down
    // and Home/End would jump the document, scrolling the editor out of view mid-resize.
    divider.addEventListener('keydown', function (e) {
      var key = e.key;
      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        if (e.preventDefault) { e.preventDefault(); }
        reset();
        return;
      }
      var dims = measure(doc, els);
      var limits = bounds(dims);
      var now = els.wrap.getBoundingClientRect().height;
      var next;
      if (key === 'ArrowUp' || key === 'Up') { next = now + KEY_STEP_PX; }
      else if (key === 'ArrowDown' || key === 'Down') { next = now - KEY_STEP_PX; }
      else if (key === 'Home') { next = limits.min; }
      else if (key === 'End') { next = limits.max; }
      else { return; }
      if (e.preventDefault) { e.preventDefault(); }
      commit(next, true);
    });

    // --- double-click reset (the in-product recovery path) -------------------
    divider.addEventListener('dblclick', function (e) {
      if (e.preventDefault) { e.preventDefault(); }
      reset();
    });

    // --- clamp triggers ------------------------------------------------------
    var pending = false;
    var inProgress = false;
    var scheduleClamp = function () {
      if (pending) { return; }
      pending = true;
      var run = function () {
        pending = false;
        if (inProgress) { return; }
        inProgress = true;
        try {
          applyCurrent(doc, false);
          syncAria(doc, divider);
        } catch (err) { /* never throw out of an observer */ }
        inProgress = false;
      };
      if (view && view.requestAnimationFrame) { view.requestAnimationFrame(run); } else { setTimeout(run, 16); }
    };

    // A ResizeObserver catches the in-page layout changes an enumerated event list misses
    // (the instructions banner collapsing, challenge instructions toggling, review's panel
    // populating after load). It must ALSO observe the wrap: appending the stdin row or the
    // Reload button into a fixed-height wrap does not change .editor-split's height.
    try {
      if (view && view.ResizeObserver) {
        var ro = new view.ResizeObserver(scheduleClamp);
        ro.observe(els.split);
        ro.observe(els.wrap);
      }
    } catch (e) { /* no observer — the window listeners below still cover the common cases */ }

    if (view && view.addEventListener) {
      view.addEventListener('resize', scheduleClamp);
      view.addEventListener('orientationchange', scheduleClamp);
    }
    // In pgzero mode the fullscreen element is .runner-stage, which leaves the divider
    // outside the top layer: invisible but still tabbable and arrow-operable. A JS-toggled
    // body class covers what `.runner-console-wrap:fullscreen` cannot. Entering or leaving
    // need not change .editor-split's box, so the observer alone would not fire.
    doc.addEventListener('fullscreenchange', function () {
      doc.body.classList.toggle('console-fullscreen', !!doc.fullscreenElement);
      scheduleClamp();
    });

    syncAria(doc, divider);
  } catch (e) {
    console.error(e);
  }
}
